/**
 * Живой поток Core: один на сеанс, а не на раздел.
 *
 * Раньше сокет создавался и уничтожался вместе с графиком. Пока график
 * был всем приложением, это было незаметно. С разделами вышло скверно:
 * каждый уход на «Главную» и возврат означал полный круг заново —
 * новый сокет, новая подписка, а в Core снятие наблюдения за
 * инструментом и повторный прогрев пятисот баров.
 *
 * И главное: заново открытый биржевой поток около двадцати секунд не
 * присылает ничего. Core в это время считает его молчащим и переходит
 * на опрос — но до перехода формирующейся свечи нет. Человек видел
 * ровно это: возвращаешься на график, а последняя, ещё не закрытая
 * свеча пропала. Измерено и в разработке, и на сервере: после каждой
 * переподписки в журнале «поток молчит, перехожу на опрос», ровно
 * двадцать секунд.
 *
 * Поток принадлежит сеансу. Пока человек внутри приложения, он жив, а
 * подписки на нём переживают переходы между разделами: `subscribe`
 * идемпотентен, и повторный вызов с тем же инструментом ничего не
 * пересоздаёт.
 *
 * Тяжёлые модули подгружаются по требованию — по той же причине, что и
 * в загрузке профиля: обычный ввоз затащил бы склад терминала в первый
 * экран каждому, кто открыл сводку.
 */

import { useEffect } from 'react';
import type { CoreStream } from '../api/stream.js';

/** Живой поток. `null` — ещё не создан или уже закрыт. */
let stream: CoreStream | null = null;

/**
 * Что сделать после обрыва.
 *
 * Ставит график: пока сокет лежал, история могла уйти вперёд, и
 * перечитать её умеет только он. Когда графика на экране нет,
 * обработчика тоже нет — и перечитывать нечего.
 */
let onReconnect: (() => void) | null = null;

export function getStream(): CoreStream | null {
  return stream;
}

export function setReconnectHandler(handler: (() => void) | null): void {
  onReconnect = handler;
}

export function useCoreStream(): void {
  useEffect(() => {
    let live = true;

    void (async () => {
      const [{ CoreStream: Stream }, { handleMessage }, store] = await Promise.all([
        import('../api/stream.js'),
        import('./stream-messages.js'),
        import('./store.js'),
      ]);

      if (!live) return;

      const created = new Stream({
        onState: (state) => store.useTerminal.getState().setConnection(state),
        onMessage: handleMessage,
        onReconnect: () => onReconnect?.(),
      });

      stream = created;
      created.connect();
    })();

    return () => {
      live = false;
      stream?.dispose();
      stream = null;
    };
  }, []);
}
